import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Activity, CheckCircle, AlertTriangle, XCircle, Clock, Users, FileText,
  Ban, ChevronDown, ChevronUp, RefreshCw, Send, Eye,
} from "lucide-react";
import { AppButton, EmptyState, ConfirmModal, Paginator } from "../ui";
import { Form, Modal } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import Shimmer from "../Common/Shimmer";
import moment from "moment";
import "./broadcast-runs.scss";

/**
 * Poll interval while any run is still live.
 *
 * 6s rather than 1s because the underlying worker sends ~50 messages a minute —
 * so a faster poll redraws the same numbers. Polling stops entirely when nothing
 * is live, which is the common case: this tab must not sit hitting the API every
 * few seconds just because someone left it open.
 */
const LIVE_POLL_MS = 6000;

const LIVE_STATUSES = ["queued", "dispatching", "sending"];

const STATUS_META = {
  queued: { label: "Queued", cls: "queued", Icon: Clock },
  dispatching: { label: "Starting", cls: "live", Icon: Activity },
  sending: { label: "Sending", cls: "live", Icon: Send },
  completed: { label: "Completed", cls: "ok", Icon: CheckCircle },
  completed_with_errors: { label: "Completed with issues", cls: "warn", Icon: AlertTriangle },
  failed: { label: "Failed", cls: "fail", Icon: XCircle },
  cancelled: { label: "Cancelled", cls: "muted", Icon: Ban },
};

const AUDIENCE_LABEL = {
  all: "All students",
  all_students: "All students",
  course: "By course",
  batch: "By batch",
  students: "Specific students",
  specific_students: "Specific students",
  external_numbers: "Pasted numbers",
  trainers: "Trainers",
  contact_groups: "Contact groups",
};

/** "1h 40m" — a 50k run takes most of a day, so minutes alone stop being useful. */
function humanDuration(minutes) {
  if (!minutes || minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/* ══════════════════════════════════════════════════════════════════════════
   Runs tab
   ══════════════════════════════════════════════════════════════════════════ */
const BroadcastRunsTab = ({ showToast }) => {
  const canManage = can("whatsapp.manage");
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [failuresFor, setFailuresFor] = useState(null);
  const limit = 15;

  // A ref, not state: the poll effect reads it without wanting to re-subscribe
  // every time the list refreshes.
  const hasLiveRef = useRef(false);

  const fetchRuns = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      const { data } = await apiClient.get("/whatsapp/runs", { params });
      setRuns(data.runs || []);
      setTotal(data.total || 0);
      hasLiveRef.current = (data.runs || []).some((r) => LIVE_STATUSES.includes(r.status));
    } catch {
      if (!quiet) showToast("Failed to load broadcasts", "error");
    }
    if (!quiet) setLoading(false);
  }, [page, statusFilter, showToast]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Poll only while something is actually moving. `quiet: true` refreshes the
  // numbers in place rather than flashing the shimmer every six seconds.
  useEffect(() => {
    const id = setInterval(() => {
      if (hasLiveRef.current) fetchRuns({ quiet: true });
    }, LIVE_POLL_MS);
    return () => clearInterval(id);
  }, [fetchRuns]);

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await apiClient.post(`/whatsapp/runs/${cancelTarget._id}/cancel`);
      showToast(
        data.inFlight > 0
          ? `Broadcast stopped. ${data.inFlight} message(s) already queued will still be sent.`
          : "Broadcast stopped.",
      );
      setCancelTarget(null);
      fetchRuns();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to cancel", "error");
    }
    setCancelling(false);
  };

  const totalPages = Math.ceil(total / limit);
  const liveCount = runs.filter((r) => LIVE_STATUSES.includes(r.status)).length;

  return (
    <div className="wa-runs">
      <div className="wa-runs__header">
        <div>
          <h3 className="wa-runs__title"><Activity size={20} /> Broadcasts</h3>
          <p className="wa-runs__subtitle">
            Every broadcast and scheduled send, with live progress. Large sends run in the
            background — you can leave this page and you&rsquo;ll be notified when one finishes.
          </p>
        </div>
        <AppButton variant="outline" size="sm" onClick={() => fetchRuns()}>
          <RefreshCw size={14} /> Refresh
        </AppButton>
      </div>

      <div className="wa-runs__toolbar">
        <Form.Select
          size="sm" className="wa-runs__filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All broadcasts</option>
          <option value="live">In progress</option>
          <option value="completed">Completed</option>
          <option value="completed_with_errors">Completed with issues</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </Form.Select>
        {liveCount > 0 && (
          <span className="wa-runs__live-pill">
            <span className="wa-runs__pulse" /> {liveCount} in progress
          </span>
        )}
        <span className="wa-runs__count">{total} total</span>
      </div>

      {loading ? (
        <Shimmer height={104} count={4} />
      ) : runs.length === 0 ? (
        <EmptyState
          icon={<Activity size={40} />}
          title="No broadcasts yet"
          description="Send a broadcast or schedule a message and it will appear here with live progress."
        />
      ) : (
        <>
          <div className="wa-runs__list">
            {runs.map((run) => (
              <RunCard
                key={run._id}
                run={run}
                expanded={expanded === run._id}
                onToggle={() => setExpanded(expanded === run._id ? null : run._id)}
                onCancel={canManage ? () => setCancelTarget(run) : undefined}
                onViewFailures={() => setFailuresFor(run)}
              />
            ))}
          </div>
          <Paginator
            page={page} totalPages={totalPages} total={total}
            pageSize={limit} onChange={setPage} infoLabel="broadcasts"
          />
        </>
      )}

      <ConfirmModal
        show={!!cancelTarget}
        onHide={() => !cancelling && setCancelTarget(null)}
        onConfirm={doCancel}
        title="Stop this broadcast"
        variant="warning"
        confirmText="Stop broadcast"
        cancelText="Keep sending"
        loading={cancelling}
        message={
          <div>
            <p>Stop <strong>{cancelTarget?.title || cancelTarget?.templateName}</strong>?</p>
            <div className="wa-alert wa-alert--warning">
              <AlertTriangle size={14} />
              {/* Stated rather than glossed: messages already handed to the queue
                  cannot be recalled, and an operator who believes otherwise will
                  not understand tomorrow's message log or bill. */}
              Messages already queued will still be delivered — WhatsApp cannot recall a
              message once it has been accepted. Everything not yet queued is stopped.
            </div>
          </div>
        }
      />

      {failuresFor && (
        <FailuresModal run={failuresFor} onClose={() => setFailuresFor(null)} showToast={showToast} />
      )}
    </div>
  );
};

/* ── Run card ───────────────────────────────────────────────────────────── */
const RunCard = ({ run, expanded, onToggle, onCancel, onViewFailures }) => {
  const meta = STATUS_META[run.status] || STATUS_META.queued;
  const { Icon } = meta;
  const p = run.progress || {};
  const isLive = LIVE_STATUSES.includes(run.status);

  // Remaining time from the worker's real throughput (~50/min), not from elapsed
  // time so far — a run that has been throttled or is still dispatching would
  // otherwise extrapolate into nonsense.
  const remaining = Math.max(0, (p.total || 0) - (p.settled || 0));
  const etaMinutes = isLive ? Math.ceil(remaining / 50) : 0;

  return (
    <div className={`wa-run-card wa-run-card--${meta.cls}`}>
      <div className="wa-run-card__head">
        <span className={`wa-run-card__status wa-run-card__status--${meta.cls}`}>
          <Icon size={13} /> {meta.label}
        </span>
        <h4 className="wa-run-card__title">{run.title || run.templateName}</h4>
        <span className="wa-run-card__when">
          {run.source === "scheduled" ? "Scheduled · " : ""}
          {moment(run.createdAt).fromNow()}
        </span>
      </div>

      <div className="wa-run-card__meta">
        <span><FileText size={12} /> {run.templateName}</span>
        <span><Users size={12} /> {AUDIENCE_LABEL[run.audienceType] || run.audienceType}</span>
        {(run.contactGroupIds || []).map((g) => (
          <span key={g._id} className="wa-run-card__group" style={{ "--cg-color": g.color || "#25D366" }}>
            {g.name}
          </span>
        ))}
        {run.createdBy?.name && <span className="wa-run-card__dim">by {run.createdBy.name}</span>}
      </div>

      {/* Progress — segmented so delivered / sent / failed / skipped are all
          visible at once, rather than one bar that hides the mix. */}
      <div className="wa-run-card__progress">
        <div className="wa-run-card__bar">
          <div className="wa-run-card__seg wa-run-card__seg--delivered" style={{ width: `${pct(p.delivered, p.total)}%` }} />
          <div className="wa-run-card__seg wa-run-card__seg--sent" style={{ width: `${pct(Math.max(0, (p.sent || 0) - (p.delivered || 0)), p.total)}%` }} />
          <div className="wa-run-card__seg wa-run-card__seg--failed" style={{ width: `${pct(p.failed, p.total)}%` }} />
          <div className="wa-run-card__seg wa-run-card__seg--skipped" style={{ width: `${pct(p.skipped, p.total)}%` }} />
        </div>
        <div className="wa-run-card__numbers">
          <strong>{(p.settled || 0).toLocaleString()}</strong>
          <span> of {(p.total || 0).toLocaleString()}</span>
          {isLive && etaMinutes > 0 && (
            <span className="wa-run-card__eta">· about {humanDuration(etaMinutes)} left</span>
          )}
        </div>
      </div>

      <div className="wa-run-card__stats">
        <Stat label="Delivered" value={p.delivered} cls="delivered" />
        <Stat label="Sent" value={p.sent} cls="sent" />
        <Stat label="Read" value={p.read} cls="read" />
        <Stat label="Failed" value={p.failed} cls="failed" />
        <Stat label="Skipped" value={p.skipped} cls="skipped" />
      </div>

      {run.error && (
        <div className="wa-alert wa-alert--warning wa-run-card__error">
          <AlertTriangle size={13} /> {run.error}
        </div>
      )}

      {/* The most common "why did only 1,000 send?" — surfaced on the card
          itself rather than buried, because the answer is a setting the operator
          can change and would otherwise never think to look for. */}
      {p.skipped > 0 && run.status !== "cancelled" && (
        <div className="wa-alert wa-alert--info wa-run-card__error">
          <AlertTriangle size={13} />
          {p.skipped.toLocaleString()} message(s) were skipped — most often the institute&rsquo;s
          daily WhatsApp limit. Raise it in Config and re-send to the remaining contacts.
        </div>
      )}

      <div className="wa-run-card__actions">
        <button onClick={onToggle}>
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Details
        </button>
        {p.failed > 0 && (
          <button onClick={onViewFailures}><Eye size={13} /> View {p.failed} failure{p.failed !== 1 ? "s" : ""}</button>
        )}
        {isLive && onCancel && (
          <button className="danger" onClick={onCancel}><Ban size={13} /> Stop</button>
        )}
      </div>

      {expanded && (
        <div className="wa-run-card__detail">
          <Row label="Started" value={run.startedAt ? moment(run.startedAt).format("MMM DD, YYYY · hh:mm A") : "—"} />
          <Row label="Finished" value={run.completedAt ? moment(run.completedAt).format("MMM DD, YYYY · hh:mm A") : "—"} />
          <Row label="Queued so far" value={(p.enqueued || 0).toLocaleString()} />
          <Row
            label="All recipients queued"
            value={p.dispatchComplete ? "Yes" : "No — still adding to the queue"}
          />
          <Row label="Language" value={run.languageCode || "en"} />
          {run.failureSamples?.length > 0 && (
            <div className="wa-run-card__samples">
              <span className="wa-run-card__samples-label">Recent failures</span>
              {run.failureSamples.slice(-5).reverse().map((f, i) => (
                <div key={`${f.phone}-${i}`} className="wa-run-card__sample">
                  <code>{f.phone}</code> <span>{f.error}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const pct = (n, total) => (total > 0 ? Math.min(100, ((n || 0) / total) * 100) : 0);

const Stat = ({ label, value, cls }) => (
  <div className={`wa-run-stat wa-run-stat--${cls}`}>
    <strong>{(value || 0).toLocaleString()}</strong>
    <span>{label}</span>
  </div>
);

const Row = ({ label, value }) => (
  <div className="wa-run-card__detail-row">
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

/* ── Failures modal ─────────────────────────────────────────────────────── */
const FailuresModal = ({ run, onClose, showToast }) => {
  const [failures, setFailures] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 50;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/whatsapp/runs/${run._id}/failures`, {
          params: { page, limit },
        });
        setFailures(data.failures || []);
        setTotal(data.total || 0);
      } catch { showToast("Failed to load failures", "error"); }
      setLoading(false);
    })();
  }, [run._id, page, showToast]);

  const copyNumbers = () => {
    navigator.clipboard?.writeText(failures.map((f) => f.toPhone).join("\n"))
      .then(() => showToast("Numbers on this page copied"))
      .catch(() => showToast("Could not copy", "error"));
  };

  return (
    <Modal show centered size="lg" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
          Failed messages — {run.title || run.templateName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Shimmer height={32} count={8} />
        ) : failures.length === 0 ? (
          <EmptyState icon={<CheckCircle size={32} />} title="No failures recorded" description="" />
        ) : (
          <>
            <div className="wa-runs__fail-toolbar">
              <span>{total.toLocaleString()} failed</span>
              <AppButton size="sm" variant="outline" onClick={copyNumbers}>Copy numbers on this page</AppButton>
            </div>
            <div className="wa-cg-table-wrap">
              <table className="wa-cg-table">
                <thead>
                  <tr><th>Phone</th><th>Error</th><th>Code</th><th>When</th></tr>
                </thead>
                <tbody>
                  {failures.map((f) => (
                    <tr key={f._id}>
                      <td className="wa-cg-table__phone">{f.toPhone}</td>
                      <td>{f.errorMessage || "—"}</td>
                      <td className="wa-cg-table__dim">{f.errorCode || "—"}</td>
                      <td className="wa-cg-table__dim">{moment(f.createdAt).format("MMM DD, hh:mm A")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginator
              page={page} totalPages={Math.ceil(total / limit)} total={total}
              pageSize={limit} onChange={setPage} infoLabel="failures"
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <AppButton variant="outline" onClick={onClose}>Close</AppButton>
      </Modal.Footer>
    </Modal>
  );
};

export default BroadcastRunsTab;
export { LIVE_STATUSES, humanDuration };
